/**
 * M-A5 — Asset upload modal.
 *
 * Sprint 131 Stream C. Uploads an asset to a skill. Supports inline content
 * (text-based assets) or storage_key references. Auto-detects size for inline.
 */

import { useState } from 'react';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useUploadAdminSkillAsset } from '@/features/skills';
import type {
  AssetInjectMode,
  AssetType,
  SkillAssetCreate,
} from '@/features/skills';
import { useI18n } from '@/i18n';
import { getErrorMessage } from '@/lib/api-client';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  skillId: string;
}

const ASSET_ID_RE = /^[a-z0-9_-]+$/;

export function AssetUploadModal({ open, onOpenChange, skillId }: Props) {
  const { t } = useI18n();
  const [assetId, setAssetId] = useState('');
  const [type, setType] = useState<AssetType>('document_template');
  const [inject, setInject] = useState<AssetInjectMode>('on_demand');
  const [content, setContent] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const isDirty =
    open &&
    (assetId.trim() !== '' ||
      content.trim() !== '' ||
      type !== 'document_template' ||
      inject !== 'on_demand');
  const discardLabels = {
    title: t('common.discardChanges.title') ?? 'Discard changes?',
    message: t('common.discardChanges.message') ?? 'You have unsaved changes. If you close now, they will be lost.',
    confirm: t('common.discardChanges.discard') ?? 'Discard',
    cancel: t('common.discardChanges.keepEditing') ?? 'Keep editing',
  };
  const requestClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const ASSET_TYPES: { value: AssetType; label: string }[] = [
    { value: 'document_template', label: t('admin.skills.assetUpload.typeDocumentTemplate') },
    { value: 'few_shot_example', label: t('admin.skills.assetUpload.typeFewShotExample') },
    { value: 'reference_document', label: t('admin.skills.assetUpload.typeReferenceDocument') },
    { value: 'schema', label: t('admin.skills.assetUpload.typeSchema') },
    { value: 'script', label: t('admin.skills.assetUpload.typeScript') },
  ];

  const INJECT_MODES: { value: AssetInjectMode; label: string }[] = [
    { value: 'on_demand', label: t('admin.skills.assetUpload.injectOnDemand') },
    { value: 'always', label: t('admin.skills.assetUpload.injectAlways') },
    { value: 'reference_only', label: t('admin.skills.assetUpload.injectReferenceOnly') },
  ];

  const upload = useUploadAdminSkillAsset(skillId);

  function reset() {
    setAssetId('');
    setType('document_template');
    setInject('on_demand');
    setContent('');
    setErrors({});
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    const e: Record<string, string> = {};
    if (!ASSET_ID_RE.test(assetId)) {
      e.assetId = t('admin.skills.assetUpload.errorAssetId');
    }
    if (!content.trim()) e.content = t('admin.skills.assetUpload.errorContentEmpty');
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    const payload: SkillAssetCreate = {
      asset_id: assetId,
      type,
      inject,
      content,
      size_bytes: new TextEncoder().encode(content).length,
    };
    try {
      await upload.mutateAsync(payload);
      toast.success(
        t('admin.skills.assetUpload.uploaded', { assetId }),
      );
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          requestClose();
          return;
        }
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="max-w-2xl"
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <DialogHeader>
          <DialogTitle>{t('admin.skills.assetUpload.title')}</DialogTitle>
          <DialogDescription>
            {t('admin.skills.assetUpload.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1 md:col-span-1">
              <Label>{t('admin.skills.assetUpload.assetId')}</Label>
              <Input
                value={assetId}
                onChange={(e) => setAssetId(e.target.value)}
                placeholder={t('admin.skills.assetUpload.assetIdPlaceholder')}
              />
              {errors.assetId && (
                <p className="text-xs text-destructive">{errors.assetId}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label>{t('admin.skills.assetUpload.type')}</Label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as AssetType)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {ASSET_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t('admin.skills.assetUpload.injectMode')}</Label>
              <select
                value={inject}
                onChange={(e) => setInject(e.target.value as AssetInjectMode)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {INJECT_MODES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <Label>{t('admin.skills.assetUpload.content')}</Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={10}
              className="font-mono text-sm"
              placeholder={t('admin.skills.assetUpload.contentPlaceholder')}
            />
            {errors.content && (
              <p className="text-xs text-destructive">{errors.content}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {t('admin.skills.assetUpload.size', { bytes: new Intl.NumberFormat().format(new TextEncoder().encode(content).length) })}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={requestClose}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={upload.isPending}>
              {upload.isPending ? t('admin.skills.assetUpload.uploading') : t('admin.skills.assetUpload.upload')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          reset();
          onOpenChange(false);
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}
